import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        try {
          const response = await fetch(`${baseUrl}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Error checking auth status:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (username, password) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const loginUrl = `${baseUrl}/api/auth/login`;

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setToken(data.access_token);
        localStorage.setItem('token', data.access_token);
        return { success: true };
      } else {
        let errorMessage = 'Ошибка входа';
        try {
          const errBody = await response.json();
          errorMessage = errBody.detail || (typeof errBody.detail === 'string' ? errBody.detail : errorMessage);
        } catch (_) {
          errorMessage = response.status === 401 ? 'Неверный логин или пароль' : `Ошибка ${response.status}`;
        }
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Load failed')) {
        return { success: false, error: 'Сервер недоступен. Запустите бэкенд (порт 8001) и обновите страницу.' };
      }
      return { success: false, error: msg || 'Ошибка сети' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const updateToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const rolePermissions = {
      'ADMIN': ['view_all', 'create_all', 'edit_all', 'delete_all', 'manage_users', 'system_settings', 'export_data'],
      'MANAGER': ['view_all', 'create_deals', 'edit_deals', 'create_clients', 'edit_clients'],
      'OPERATOR': ['view_all'],
      'CASHIER': ['create_clients', 'create_deals', 'view_own']
    };

    return rolePermissions[user.role]?.includes(permission) || false;
  };

  const isRole = (role) => {
    return user?.role === role;
  };

  const isAdmin = () => isRole('ADMIN');
  const isManager = () => isRole('MANAGER') || isRole('ADMIN');
  const isOperator = () => isRole('OPERATOR');
  const isCashier = () => isRole('CASHIER');

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    updateToken,
    hasPermission,
    isRole,
    isAdmin,
    isManager,
    isOperator,
    isCashier,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};